// src/features/transfer/api/create-withdraw.ts

import { getTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { EventType } from '@/types';
import { Entity } from '@/types/api';
import { ErrorCode, ErrorMsgs, OrderStatus } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import { bigIntToNum } from '@/utils/value-format';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  encodeAbiParameters,
  EncodeAbiParametersReturnType,
  keccak256,
} from 'viem';
import { signMessage } from 'viem/accounts';
import { z } from 'zod';
import { MutationConfig } from '../api-clients/react-query';
import { privateApi } from '../api-clients/rest-client';

const createWithdrawInputSchema = z.object({
  token: z.string(),
  size: z.bigint(),
  chainId: z.number(),
  execute: z.boolean(),
  recipient: z.string().optional(),
});

type CreateWithdrawParams = z.infer<typeof createWithdrawInputSchema>;

type CreateWithdrawResponse = Entity<{
  id: string;
  status: string; // NEW
}>;

const createWithdraw = async (
  data: CreateWithdrawParams,
  email?: string,
): Promise<CreateWithdrawResponse> => {
  if (!data.recipient) {
    throw new Error('Invalid recipient.');
  }

  const { getAuthParams, nextNonce } = useAccountStore.getState();
  const authParams = getAuthParams();

  if (!authParams?.signingKey) {
    console.error('No signing key found');
    throw new Error('No signing key found');
  }

  const time = useAccountStore.getState().getAccurateTime();
  const address = useAccountStore.getState().address || '';
  const nonce = nextNonce();

  let encodedOrder: EncodeAbiParametersReturnType;

  try {
    encodedOrder = encodeAbiParameters(
      [
        { name: 'time', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'isDeposit', type: 'bool' },
        { name: 'account', type: 'address' },
        { name: 'recipient', type: 'address' },
        { name: 'token', type: 'address' },
        { name: 'size', type: 'uint256' },
        { name: 'chainId', type: 'uint256' },
      ],
      [
        BigInt(time),
        BigInt(nonce),
        false,
        authParams.address as `0x${string}`,
        data.recipient as `0x${string}`,
        data.token as `0x${string}`,
        data.size,
        BigInt(data.chainId),
      ],
    );
  } catch (err) {
    toast.error(
      'message' in (err as Error)
        ? (err as Error).message.split('.')[0] + '.'
        : 'Something went wrong. Please check your input and try again.',
    );
    return Promise.reject();
  }

  const signature = await signMessage({
    message: { raw: keccak256(encodedOrder) },
    privateKey: authParams.signingKey,
  });

  const body = {
    order: {
      ...data,
      size: Number(data.size),
      nonce,
      time,
    },
    execute: data.execute,
    signature,
  };

  const shouldWaitValidation =
    !data.execute && bigIntToNum(data.size, COLLATERAL_DECIMALS) < 50000;

  const promise = privateApi.post('/transfer/withdraw', body);

  const toastPromise = promise.then((res) => {
    const { id: orderId } = res;
    sendEvent(EventType.WITHDRAW_INITIATED, {
      amount: bigIntToNum(data.size, COLLATERAL_DECIMALS),
      email,
    });
    return new Promise((resolve, reject) => {
      let pollIntervalId: NodeJS.Timeout;
      let subscriberTimeoutId: NodeJS.Timeout;
      let unsubscribe: () => void;

      const cleanup = () => {
        clearTimeout(subscriberTimeoutId);
        clearInterval(pollIntervalId);
        unsubscribe();
      };

      unsubscribe = useTransfersStore.subscribe((state, prevState) => {
        const transfers = state.transfers[address] ?? {};
        if (orderId in transfers) {
          cleanup();
          if (
            transfers[orderId].status === OrderStatus.NEW ||
            transfers[orderId].status === OrderStatus.PROCESSING
          ) {
            if (shouldWaitValidation)
              toast.loading('Waiting for order validation...');
            resolve(transfers[orderId]);
          } else if (transfers[orderId].status === OrderStatus.REJECTED) {
            reject(transfers[orderId].code as ErrorCode);
          }
        }
      });

      subscriberTimeoutId = setTimeout(() => {
        cleanup();
        reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
      }, 30_000); // 30 seconds timeout

      pollIntervalId = setInterval(() => {
        getTransfers({ id: orderId }).then((res) =>
          res.forEach((data) => {
            const transfer = transferResponseToTransfer(data);
            handleTransferForStore(transfer);
          }),
        );
      }, 2_000);
    });
  });

  toast.promise(toastPromise, {
    loading: 'Requesting withdrawal...',
    success: (
      <span>
        Successfully requested withdrawal: see{' '}
        <button
          className="underline"
          onClick={() => {
            const navigate = new CustomEvent('app:navigate', {
              detail: { pathname: '/portfolio', search: '?view=transfers' },
            });
            window.dispatchEvent(navigate);
          }}
        >
          Transfers
        </button>
      </span>
    ),
    error: (error) => `Withdraw failed: ${ErrorMsgs[error as ErrorCode]}`,
  });

  return promise;
};

type UseCreateWithdrawOptions = {
  mutationConfig?: MutationConfig<typeof createWithdraw>;
};

export const useCreateWithdraw = ({
  mutationConfig,
}: UseCreateWithdrawOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  const { email } = usePrivyAccount();

  return useMutation({
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['withdrawOrders'],
      });
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: (params) => createWithdraw(params, email),
  });
};
